import { Injectable } from '@angular/core';
import { BehaviorSubject, NEVER, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { LegoRestApiDataService } from 'src/app/services';

interface IRevisionState {
  // undefined until a response lands, so "not loaded yet" stays distinguishable from
  // "loaded and Teamcenter holds none". Both used to be [], which rendered identically.
  mCodes?: string[];
  loading: boolean;
  error?: string;
}

// Partial<> so the lookup models a missing revision; an index signature does not.
type Revisions = Partial<Record<string, IRevisionState>>;

function stateFor(revisions: Revisions, revisionId: string): IRevisionState {
  return revisions[revisionId] ?? { loading: false };
}

/**
 * Keyed by revision id, because loading and error here are *per revision* — the
 * Akita version tracked the same thing across three parallel structures (an
 * entity collection plus `loadingForIds: string[]` and `errors: IError[]`).
 */
@Injectable({ providedIn: 'root' })
export class RevisionMCodesService {
  private readonly revisions = new BehaviorSubject<Revisions>({});

  public constructor(private readonly legoRestApiDataService: LegoRestApiDataService) {}

  /** Emits undefined until a response lands for this revision. */
  public selectMCodes(revisionId: string): Observable<string[] | undefined> {
    return this.select(revisionId, ({ mCodes }) => mCodes);
  }

  public selectLoading(revisionId: string): Observable<boolean> {
    return this.select(revisionId, ({ loading }) => loading);
  }

  public selectError(revisionId: string): Observable<string | undefined> {
    return this.select(revisionId, ({ error }) => error);
  }

  public load(itemId: string, revisionId: string) {
    this.patch(revisionId, { error: undefined, loading: true, mCodes: undefined });

    return this.legoRestApiDataService.getMCodes(itemId, revisionId).pipe(
      catchError(() => {
        this.patch(revisionId, {
          error: `Could not load 2D M-Codes for ${itemId} revision ${revisionId}`,
          loading: false,
        });

        return NEVER;
      }),
      tap((mCodes) => {
        // getMCodes resolves to undefined when the response carries no _DATA envelope. That
        // is a failed lookup, not a revision with zero M-codes, so it becomes an error rather
        // than an empty list silently rendering as "no M-codes".
        if (mCodes === undefined) {
          this.patch(revisionId, {
            error: `No 2D M-Code data returned for ${itemId} revision ${revisionId}`,
            loading: false,
          });

          return;
        }

        this.patch(revisionId, { error: undefined, loading: false, mCodes });
      }),
      map(() => undefined)
    );
  }

  public reset() {
    this.revisions.next({});
  }

  private select<R>(revisionId: string, project: (state: IRevisionState) => R) {
    return this.revisions.pipe(
      map((revisions) => project(stateFor(revisions, revisionId))),
      distinctUntilChanged()
    );
  }

  private patch(revisionId: string, partial: Partial<IRevisionState>) {
    const current = this.revisions.value;

    this.revisions.next({
      ...current,
      [revisionId]: { ...stateFor(current, revisionId), ...partial },
    });
  }
}
